import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Server always returns 200 — if we get here, it's a network/500 issue.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/start" className="inline-flex items-center gap-2 text-[#486a8f] text-[13px] mb-8 hover:underline" data-testid="forgot-back-link">
          <ArrowLeft size={14} /> Back to login
        </Link>
        <div className="bg-white rounded-2xl p-8 shadow-[0_10px_40px_-20px_rgba(15,23,36,0.15)] border border-[#eadfc9]">
          {!sent ? (
            <>
              <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#486a8f] mb-3">Reset password</p>
              <h1 className="font-display text-[26px] text-[#0f1724] font-semibold tracking-tight leading-tight mb-3">
                Forgot your password?
              </h1>
              <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mb-6">
                Enter the email tied to your account and we'll send a reset link. The link expires in 60 minutes.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="font-mono-eyebrow text-[10px] text-[#486a8f] block mb-2">EMAIL</label>
                  <div className="relative">
                    <Input
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="h-12 pl-10"
                      required
                      autoFocus
                    />
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#486a8f]" />
                  </div>
                </div>
                <Button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full h-12 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[14px]"
                >
                  {loading ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending…</> : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success-state">
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#3e6b52] flex items-center justify-center">
                <CheckCircle2 size={28} className="text-white" />
              </div>
              <p className="font-mono-eyebrow text-[10px] uppercase tracking-wider text-[#3e6b52] mb-3">Check your inbox</p>
              <h2 className="font-display text-[22px] text-[#0f1724] font-semibold tracking-tight leading-tight mb-3">
                Reset link sent
              </h2>
              <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mb-6">
                If an account exists for <span className="font-medium text-[#0f1724]">{email}</span>, you'll get an email within a minute. The link expires in 60 minutes.
              </p>
              <p className="font-body text-[12px] text-[#6b7280] leading-relaxed">
                Didn't get it? Check your spam folder, or <Link to="/forgot-password" onClick={() => setSent(false)} className="text-[#486a8f] hover:underline">try a different email</Link>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
